import { useId, useState } from "react";
import { CalendarDays, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
    Button,
    Calendar,
    Checkbox,
    Label,
    Popover,
    PopoverContent,
    PopoverTrigger,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@athena/ui";
import { isFiltered, type AudienceFilter } from "../helpers/audienceFilter";
import { formatCalendarDate, parseCalendarDate, toCalendarDate } from "../helpers/client";

/** The "any" choice in a select. Radix will not take an empty value, and no KIPU value looks like this. */
const ANY = "__any__";

/** The earliest year the discharge calendar's year dropdown goes back to. */
const FIRST_DISCHARGE_MONTH = new Date(2000, 0);

/**
 * The filters that open a popover, dressed as the Select trigger beside them: an outline button is the
 * page's grey with a shadow, which made them look disabled.
 */
const POPOVER_TRIGGER =
    "border-input bg-card hover:bg-card hover:text-foreground h-10 w-full justify-between rounded-md px-3 text-sm font-normal tracking-normal normal-case shadow-none";

interface AudienceFiltersProps {
    filter: AudienceFilter;
    onChange: (filter: AudienceFilter) => void;
    /** The locations on file, from `filterChoices`. */
    locations: string[];
    /** The sexes on file, from `filterChoices`. */
    sexes: string[];
}

/**
 * Who a broadcast is for, before anyone is unchecked: one or more locations, a sex, and a discharge
 * date range. Each is "any" until set.
 *
 * The discharge date is one picker for the whole range: the first click picks a single day, a second
 * click makes it a range from one to the other, and clicking a lone selected day again clears it.
 */
export function AudienceFilters({ filter, onChange, locations, sexes }: AudienceFiltersProps) {
    const { t } = useTranslation();
    const ids = useId();
    const [
        calendarOpen,
        setCalendarOpen,
    ] = useState<boolean>(false);
    const set = (changes: AudienceFilter) => onChange({ ...filter, ...changes });
    const chosenLocations = filter.locations ?? [];
    const toggleLocation = (location: string, checked: boolean) => {
        const next = checked
            ? [
                  ...chosenLocations,
                  location,
              ]
            : chosenLocations.filter((chosen) => chosen !== location);
        set({ locations: next.length > 0 ? next : undefined });
    };
    const locationText =
        chosenLocations.length === 0
            ? t("admin.alumni.broadcast.filters.anyLocation")
            : chosenLocations.length === 1
              ? chosenLocations[0]
              : t("admin.alumni.broadcast.filters.locationCount", { count: chosenLocations.length });
    const { dischargedFrom: from, dischargedTo: to } = filter;
    const range = from ? { from: parseCalendarDate(from), to: to ? parseCalendarDate(to) : undefined } : undefined;
    const dischargeText = !from
        ? t("admin.alumni.broadcast.filters.anyDate")
        : !to || to === from
          ? formatCalendarDate(from)
          : t("admin.alumni.broadcast.filters.dateRange", {
                from: formatCalendarDate(from),
                to: formatCalendarDate(to),
            });

    return (
        <div className="flex flex-col gap-4">
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                <div className="flex flex-col gap-2">
                    <Label htmlFor={`${ids}-location`}>{t("admin.alumni.broadcast.filters.location")}</Label>
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button
                                id={`${ids}-location`}
                                variant="outline"
                                aria-label={`${t("admin.alumni.broadcast.filters.location")}: ${locationText}`}
                                className={POPOVER_TRIGGER}
                                rightIcon={<ChevronDown className="h-4 w-4 opacity-50" />}
                            >
                                <span className="truncate">{locationText}</span>
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-(--radix-popover-trigger-width) p-2" align="start">
                            <ul className="flex max-h-72 flex-col overflow-y-auto">
                                {locations.map((location, index) => (
                                    <li key={location}>
                                        <Label
                                            htmlFor={`${ids}-location-${index}`}
                                            className="hover:bg-muted flex cursor-pointer items-center gap-2 rounded-sm px-2 py-2 font-normal"
                                        >
                                            <Checkbox
                                                id={`${ids}-location-${index}`}
                                                checked={chosenLocations.includes(location)}
                                                onCheckedChange={(checked) =>
                                                    toggleLocation(location, checked === true)
                                                }
                                            />
                                            {location}
                                        </Label>
                                    </li>
                                ))}
                            </ul>
                            {chosenLocations.length > 0 ? (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="mt-2 w-auto"
                                    onClick={() => set({ locations: undefined })}
                                >
                                    {t("admin.alumni.broadcast.filters.anyLocation")}
                                </Button>
                            ) : null}
                        </PopoverContent>
                    </Popover>
                </div>
                <div className="flex flex-col gap-2">
                    <Label htmlFor={`${ids}-sex`}>{t("admin.alumni.broadcast.filters.sex")}</Label>
                    <Select
                        value={filter.sex ?? ANY}
                        onValueChange={(value) => set({ sex: value === ANY ? undefined : value })}
                    >
                        <SelectTrigger id={`${ids}-sex`}>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value={ANY}>{t("admin.alumni.broadcast.filters.anySex")}</SelectItem>
                            {sexes.map((sex) => (
                                <SelectItem key={sex} value={sex}>
                                    {sex}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="flex flex-col gap-2">
                    <Label htmlFor={`${ids}-discharge`}>{t("admin.alumni.broadcast.filters.dischargeDate")}</Label>
                    <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                        <PopoverTrigger asChild>
                            <Button
                                id={`${ids}-discharge`}
                                variant="outline"
                                aria-label={`${t("admin.alumni.broadcast.filters.dischargeDate")}: ${dischargeText}`}
                                className={POPOVER_TRIGGER}
                                rightIcon={<CalendarDays className="h-4 w-4 opacity-50" />}
                            >
                                <span className="truncate">{dischargeText}</span>
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-fit p-3" align="start">
                            <Calendar
                                mode="range"
                                showOutsideDays={false}
                                captionLayout="dropdown"
                                startMonth={FIRST_DISCHARGE_MONTH}
                                endMonth={new Date()}
                                defaultMonth={range?.from}
                                selected={range}
                                onSelect={(selected) =>
                                    set({
                                        dischargedFrom: selected?.from ? toCalendarDate(selected.from) : undefined,
                                        dischargedTo: selected?.to ? toCalendarDate(selected.to) : undefined,
                                    })
                                }
                            />
                            {from ? (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="mt-2 w-auto"
                                    onClick={() => set({ dischargedFrom: undefined, dischargedTo: undefined })}
                                >
                                    {t("admin.alumni.broadcast.filters.anyDate")}
                                </Button>
                            ) : null}
                        </PopoverContent>
                    </Popover>
                </div>
            </div>
            {isFiltered(filter) ? (
                <Button variant="ghost" size="sm" className="w-auto self-start" onClick={() => onChange({})}>
                    {t("admin.alumni.broadcast.filters.clear")}
                </Button>
            ) : null}
        </div>
    );
}
